import { getSupabaseClient } from "@veorest/api-client";
import type { TranslationKey } from "../i18n";
import type { PartnerDashboardDateRange } from "./partnerDashboardDate";
import type { PartnerOrderStage } from "./partnerOrders";

// =============================================================================
// Partner reports & analytics — typed wrappers for migration 165 RPCs.
// All money values are plain numbers in the order/client currency (IQD).
// Range is converted to half-open [from, to) instants (SQL uses >= / <).
// =============================================================================

export interface RangeInstants {
  fromIso: string;
  toIso: string;
}

export function rangeToInstants(range: PartnerDashboardDateRange): RangeInstants {
  return {
    fromIso: range.start.toISOString(),
    // end is inclusive (23:59:59.999) → SQL uses < to, so add 1ms.
    toIso: new Date(range.end.getTime() + 1).toISOString(),
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function str(v: unknown, fallback = ""): string {
  if (v == null) return fallback;
  return String(v);
}

function num(v: unknown, fallback = 0): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function nullableStr(v: unknown): string | null {
  return v == null ? null : String(v);
}

function mapRecords(value: unknown, key: string): Record<string, unknown>[] {
  const row = asRecord(value) ?? {};
  const list = Array.isArray(row[key]) ? row[key] : [];
  return list
    .map((item) => asRecord(item))
    .filter((r): r is Record<string, unknown> => !!r);
}

// ── Clients overview (Clients table) ─────────────────────────────────────────

export interface PartnerClientOverviewRow {
  id: string;
  name: string;
  slug: string;
  username: string | null;
  owner_email: string | null;
  tenant_status: string;
  phone: string | null;
  address: string | null;
  logo_url: string | null;
  currency: string;
  client_type: string;
  controlling_partner_id: string | null;
  client_type_status: string | null;
  link_status: string | null;
  dispatch_authority: string | null;
  is_primary_dispatcher: boolean;
  linked_at: string | null;
  partner_fee_type: string | null;
  partner_fee_amount: number | null;
  settlement_mode: string | null;
  cash_settlement_target: string | null;
  orders_count: number;
  completed_count: number;
  cancelled_count: number;
  delivery_fees_total: number;
  delivery_fees_customer: number;
  delivery_fees_client: number;
  client_charges_total: number;
  outstanding_pending: number;
  active_orders_count: number;
  last_order_at: string | null;
  active_drivers_count: number;
}

function mapClientOverview(raw: Record<string, unknown>): PartnerClientOverviewRow {
  return {
    id: str(raw.id),
    name: str(raw.name, "Business"),
    slug: str(raw.slug),
    username: nullableStr(raw.username),
    owner_email: nullableStr(raw.owner_email),
    tenant_status: str(raw.tenant_status, "unknown"),
    phone: nullableStr(raw.phone),
    address: nullableStr(raw.address),
    logo_url: nullableStr(raw.logo_url),
    currency: str(raw.currency, "IQD"),
    client_type: str(raw.client_type),
    controlling_partner_id: nullableStr(raw.controlling_partner_id),
    client_type_status: nullableStr(raw.client_type_status),
    link_status: nullableStr(raw.link_status),
    dispatch_authority: nullableStr(raw.dispatch_authority),
    is_primary_dispatcher: Boolean(raw.is_primary_dispatcher),
    linked_at: nullableStr(raw.linked_at),
    partner_fee_type: nullableStr(raw.partner_fee_type),
    partner_fee_amount:
      raw.partner_fee_amount == null ? null : num(raw.partner_fee_amount),
    settlement_mode: nullableStr(raw.settlement_mode),
    cash_settlement_target: nullableStr(raw.cash_settlement_target),
    orders_count: num(raw.orders_count),
    completed_count: num(raw.completed_count),
    cancelled_count: num(raw.cancelled_count),
    delivery_fees_total: num(raw.delivery_fees_total),
    delivery_fees_customer: num(raw.delivery_fees_customer),
    delivery_fees_client: num(raw.delivery_fees_client),
    client_charges_total: num(raw.client_charges_total),
    outstanding_pending: num(raw.outstanding_pending),
    active_orders_count: num(raw.active_orders_count),
    last_order_at: nullableStr(raw.last_order_at),
    active_drivers_count: num(raw.active_drivers_count),
  };
}

export async function fetchPartnerClientsOverview(
  partnerId: string,
  range?: PartnerDashboardDateRange
): Promise<PartnerClientOverviewRow[]> {
  const supabase = getSupabaseClient();
  const fromIso = range ? rangeToInstants(range).fromIso : null;
  const toIso = range ? rangeToInstants(range).toIso : null;
  const { data, error } = await supabase.rpc("partner_clients_overview", {
    p_partner_id: partnerId,
    p_from: fromIso,
    p_to: toIso,
  });
  if (error) throw error;
  return mapRecords(data, "tenants").map(mapClientOverview).filter((c) => !!c.id);
}

// ── Client report (KPIs + series + driver activity) ──────────────────────────

export interface ClientReportKpis {
  orders_total: number;
  orders_completed: number;
  orders_cancelled: number;
  orders_pickup: number;
  orders_delivery: number;
  delivery_income: number;
  delivery_income_customer: number;
  delivery_income_client: number;
  client_charges: number;
  outstanding_pending: number;
  avg_delivery_fee: number | null;
  avg_delivery_km: number | null;
  avg_prep_minutes: number | null;
  legacy_unpriced_count: number;
}

export interface ReportSeriesPoint {
  day: string;
  orders: number;
  delivery_income: number;
  client_charges: number;
}

export interface ReportDriverRow {
  driver_id: string;
  name: string;
  trips: number;
  km: number;
  last_trip_at: string | null;
}

export interface PartnerClientReport {
  kpis: ClientReportKpis;
  series: ReportSeriesPoint[];
  by_driver: ReportDriverRow[];
}

function mapKpis(raw: Record<string, unknown> | null): ClientReportKpis {
  const r = raw ?? {};
  return {
    orders_total: num(r.orders_total),
    orders_completed: num(r.orders_completed),
    orders_cancelled: num(r.orders_cancelled),
    orders_pickup: num(r.orders_pickup),
    orders_delivery: num(r.orders_delivery),
    delivery_income: num(r.delivery_income),
    delivery_income_customer: num(r.delivery_income_customer),
    delivery_income_client: num(r.delivery_income_client),
    client_charges: num(r.client_charges),
    outstanding_pending: num(r.outstanding_pending),
    avg_delivery_fee: r.avg_delivery_fee == null ? null : num(r.avg_delivery_fee),
    avg_delivery_km: r.avg_delivery_km == null ? null : num(r.avg_delivery_km),
    avg_prep_minutes: r.avg_prep_minutes == null ? null : num(r.avg_prep_minutes),
    legacy_unpriced_count: num(r.legacy_unpriced_count),
  };
}

function mapSeries(value: unknown): ReportSeriesPoint[] {
  const list = Array.isArray(value) ? value : [];
  return list
    .map((item) => asRecord(item))
    .filter((r): r is Record<string, unknown> => !!r)
    .map((r) => ({
      day: str(r.day),
      orders: num(r.orders),
      delivery_income: num(r.delivery_income),
      client_charges: num(r.client_charges),
    }));
}

function mapReportDrivers(value: unknown): ReportDriverRow[] {
  const list = Array.isArray(value) ? value : [];
  return list
    .map((item) => asRecord(item))
    .filter((r): r is Record<string, unknown> => !!r)
    .map((r) => ({
      driver_id: str(r.driver_id),
      name: str(r.name, "Driver"),
      trips: num(r.trips),
      km: num(r.km),
      last_trip_at: nullableStr(r.last_trip_at),
    }));
}

async function fetchClientReportRaw(
  partnerId: string,
  tenantId: string,
  range: PartnerDashboardDateRange
): Promise<PartnerClientReport> {
  const supabase = getSupabaseClient();
  const { fromIso, toIso } = rangeToInstants(range);
  const { data, error } = await supabase.rpc("partner_client_report", {
    p_partner_id: partnerId,
    p_tenant_id: tenantId,
    p_from: fromIso,
    p_to: toIso,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  return {
    kpis: mapKpis(asRecord(row.kpis)),
    series: mapSeries(row.series),
    by_driver: mapReportDrivers(row.by_driver),
  };
}

export { fetchClientReportRaw as fetchPartnerClientReport };

// ── Client driver activity (orders-driven; safe fields only) ─────────────────
// 242: partner-managed clients carry NO driver ownership/links — this report
// reads deliveries (orders) + driver profile fields only, never rosters.

export interface ClientDriverActivityRow {
  driver_id: string;
  name: string;
  vehicle_type: string | null;
  vehicle_number: string | null;
  vehicle_color: string | null;
  trips: number;
  km: number;
  last_trip_at: string | null;
}

export async function fetchPartnerClientDriverActivity(
  partnerId: string,
  tenantId: string,
  range: PartnerDashboardDateRange
): Promise<ClientDriverActivityRow[]> {
  const supabase = getSupabaseClient();
  const { fromIso, toIso } = rangeToInstants(range);
  const { data, error } = await supabase.rpc("partner_client_driver_activity", {
    p_partner_id: partnerId,
    p_tenant_id: tenantId,
    p_from: fromIso,
    p_to: toIso,
  });
  if (error) throw error;
  return mapRecords(data, "drivers").map((raw) => ({
    driver_id: str(raw.driver_id),
    name: str(raw.name),
    vehicle_type: nullableStr(raw.vehicle_type),
    vehicle_number: nullableStr(raw.vehicle_number),
    vehicle_color: nullableStr(raw.vehicle_color),
    trips: num(raw.trips),
    km: num(raw.km),
    last_trip_at: nullableStr(raw.last_trip_at),
  }));
}

// ── Client orders report (paged) ─────────────────────────────────────────────

export interface ClientOrderRow {
  id: string;
  tenant_id: string;
  status: string;
  fulfillment_type: string;
  stage: PartnerOrderStage;
  total: number;
  subtotal: number;
  currency: string;
  customer_name: string;
  customer_phone: string | null;
  customer_address: string | null;
  assigned_driver_id: string | null;
  driver_name: string | null;
  partner_accepted_at: string | null;
  ready_at: string | null;
  prep_minutes: number | null;
  delivery_fee: number | null;
  delivery_fee_responsibility: string | null;
  delivery_km: number | null;
  partner_fee_type: string | null;
  partner_fee_amount: number | null;
  partner_settlement_mode: string | null;
  payment_method: string | null;
  payment_status: string | null;
  pickup_payment_mode: string | null;
  created_at: string | null;
  updated_at: string | null;
}

const STAGES: readonly PartnerOrderStage[] = [
  "pending",
  "processing",
  "ready",
  "in_delivery",
  "completed",
  "cancelled",
];

function asStage(v: unknown): PartnerOrderStage {
  const s = str(v, "pending") as PartnerOrderStage;
  return STAGES.includes(s) ? s : "pending";
}

function mapClientOrder(raw: Record<string, unknown>): ClientOrderRow {
  return {
    id: str(raw.id),
    tenant_id: str(raw.tenant_id),
    status: str(raw.status),
    fulfillment_type: str(raw.fulfillment_type, "delivery"),
    stage: asStage(raw.stage),
    total: num(raw.total),
    subtotal: num(raw.subtotal),
    currency: str(raw.currency, "IQD"),
    customer_name: str(raw.customer_name, "Guest"),
    customer_phone: nullableStr(raw.customer_phone),
    customer_address: nullableStr(raw.customer_address),
    assigned_driver_id: nullableStr(raw.assigned_driver_id),
    driver_name: nullableStr(raw.driver_name),
    partner_accepted_at: nullableStr(raw.partner_accepted_at),
    ready_at: nullableStr(raw.ready_at),
    prep_minutes: raw.prep_minutes == null ? null : num(raw.prep_minutes),
    delivery_fee: raw.delivery_fee == null ? null : num(raw.delivery_fee),
    delivery_fee_responsibility: nullableStr(raw.delivery_fee_responsibility),
    delivery_km: raw.delivery_km == null ? null : num(raw.delivery_km),
    partner_fee_type: nullableStr(raw.partner_fee_type),
    partner_fee_amount:
      raw.partner_fee_amount == null ? null : num(raw.partner_fee_amount),
    partner_settlement_mode: nullableStr(raw.partner_settlement_mode),
    payment_method: nullableStr(raw.payment_method),
    payment_status: nullableStr(raw.payment_status),
    pickup_payment_mode: nullableStr(raw.pickup_payment_mode),
    created_at: nullableStr(raw.created_at),
    updated_at: nullableStr(raw.updated_at),
  };
}

export interface ClientOrdersPage {
  orders: ClientOrderRow[];
  total: number;
}

export async function fetchPartnerClientOrders(
  partnerId: string,
  tenantId: string,
  range: PartnerDashboardDateRange,
  opts?: {
    stage?: PartnerOrderStage | "all" | null;
    fulfillment?: "delivery" | "pickup" | "all" | null;
    query?: string;
    limit?: number;
    offset?: number;
  }
): Promise<ClientOrdersPage> {
  const supabase = getSupabaseClient();
  const { fromIso, toIso } = rangeToInstants(range);
  const stage = opts?.stage && opts.stage !== "all" ? opts.stage : null;
  const fulfillment =
    opts?.fulfillment && opts.fulfillment !== "all" ? opts.fulfillment : null;
  const { data, error } = await supabase.rpc("partner_client_orders", {
    p_partner_id: partnerId,
    p_tenant_id: tenantId,
    p_from: fromIso,
    p_to: toIso,
    p_stage: stage,
    p_fulfillment: fulfillment,
    p_query: opts?.query?.trim() ? opts.query.trim() : null,
    p_limit: opts?.limit ?? 25,
    p_offset: opts?.offset ?? 0,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  const list = Array.isArray(row.orders) ? row.orders : [];
  return {
    orders: list
      .map((item) => asRecord(item))
      .filter((r): r is Record<string, unknown> => !!r)
      .map(mapClientOrder),
    total: num(row.total, list.length),
  };
}

// ── Client statement ─────────────────────────────────────────────────────────

export interface ClientStatementTotals {
  charged_total: number;
  settled_total: number;
  instant_total: number;
  pending_in_range: number;
  outstanding_pending: number;
  cancelled_fee_count: number;
  /** 170: fee rows voided (order declined/cancelled after recording) in range. */
  voided_total: number;
  voided_count: number;
}

export interface ClientStatementLedgerRow {
  id: string;
  order_id: string;
  fee_type: string;
  fee_amount: number;
  order_total: number | null;
  settlement_mode: string;
  settlement_status: string;
  created_at: string | null;
  settled_at: string | null;
  /** 170: set when the fee row was voided instead of charged. */
  voided_at: string | null;
  void_reason: string | null;
  order_status: string | null;
  order_stage: PartnerOrderStage | null;
  customer_name: string | null;
}

export interface ClientStatementFeeMixRow {
  bucket: string;
  orders: number;
  fees_total: number;
}

export interface ClientStatement {
  totals: ClientStatementTotals;
  ledger: ClientStatementLedgerRow[];
  fee_mix: ClientStatementFeeMixRow[];
  wallet: { balance: number; pending_credit: number };
}

export async function fetchPartnerClientStatement(
  partnerId: string,
  tenantId: string,
  range: PartnerDashboardDateRange
): Promise<ClientStatement> {
  const supabase = getSupabaseClient();
  const { fromIso, toIso } = rangeToInstants(range);
  const { data, error } = await supabase.rpc("partner_client_statement", {
    p_partner_id: partnerId,
    p_tenant_id: tenantId,
    p_from: fromIso,
    p_to: toIso,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  const totals = asRecord(row.totals) ?? {};
  const ledgerList = Array.isArray(row.ledger) ? row.ledger : [];
  const mixList = Array.isArray(row.fee_mix) ? row.fee_mix : [];
  const wallet = asRecord(row.wallet) ?? {};
  return {
    totals: {
      charged_total: num(totals.charged_total),
      settled_total: num(totals.settled_total),
      instant_total: num(totals.instant_total),
      pending_in_range: num(totals.pending_in_range),
      outstanding_pending: num(totals.outstanding_pending),
      cancelled_fee_count: num(totals.cancelled_fee_count),
      voided_total: num(totals.voided_total),
      voided_count: num(totals.voided_count),
    },
    ledger: ledgerList
      .map((item) => asRecord(item))
      .filter((r): r is Record<string, unknown> => !!r)
      .map((r) => ({
        id: str(r.id),
        order_id: str(r.order_id),
        fee_type: str(r.fee_type),
        fee_amount: num(r.fee_amount),
        order_total: r.order_total == null ? null : num(r.order_total),
        settlement_mode: str(r.settlement_mode),
        settlement_status: str(r.settlement_status),
        created_at: nullableStr(r.created_at),
        settled_at: nullableStr(r.settled_at),
        voided_at: nullableStr(r.voided_at),
        void_reason: nullableStr(r.void_reason),
        order_status: nullableStr(r.order_status),
        order_stage: r.order_stage == null ? null : asStage(r.order_stage),
        customer_name: nullableStr(r.customer_name),
      })),
    fee_mix: mixList
      .map((item) => asRecord(item))
      .filter((r): r is Record<string, unknown> => !!r)
      .map((r) => ({
        bucket: str(r.bucket),
        orders: num(r.orders),
        fees_total: num(r.fees_total),
      })),
    wallet: {
      balance: num(wallet.balance),
      pending_credit: num(wallet.pending_credit),
    },
  };
}

export async function settlePartnerLedgerEntry(
  partnerId: string,
  ledgerId: string
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc("partner_settle_ledger_entry", {
    p_partner_id: partnerId,
    p_ledger_id: ledgerId,
  });
  if (error) throw error;
}

// ── Partner wallet (charge collection state) ─────────────────────────────────

export interface PartnerWalletLedgerRow {
  id: string;
  order_id: string;
  tenant_id: string;
  tenant_name: string;
  fee_type: string;
  fee_amount: number;
  order_total: number | null;
  settlement_mode: string;
  settlement_status: string;
  created_at: string | null;
}

export interface PartnerWalletSnapshot {
  balance: number;
  pending_credit: number;
  recent_ledger: PartnerWalletLedgerRow[];
}

export async function fetchPartnerWallet(
  partnerId: string
): Promise<PartnerWalletSnapshot> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_get_wallet", {
    p_partner_id: partnerId,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  const wallet = asRecord(row.wallet) ?? {};
  const recent = Array.isArray(row.recent_ledger) ? row.recent_ledger : [];
  return {
    balance: num(wallet.balance),
    pending_credit: num(wallet.pending_credit),
    recent_ledger: recent
      .map((item) => asRecord(item))
      .filter((r): r is Record<string, unknown> => !!r)
      .map((r) => ({
        id: str(r.id),
        order_id: str(r.order_id),
        tenant_id: str(r.tenant_id),
        tenant_name: str(r.tenant_name, "Client"),
        fee_type: str(r.fee_type),
        fee_amount: num(r.fee_amount),
        order_total: r.order_total == null ? null : num(r.order_total),
        settlement_mode: str(r.settlement_mode),
        settlement_status: str(r.settlement_status),
        created_at: nullableStr(r.created_at),
      })),
  };
}

// ── Analytics summary ────────────────────────────────────────────────────────

export interface AnalyticsKpis extends ClientReportKpis {
  cod_total: number;
  wallet_balance: number;
  pending_added_in_range: number;
  cancelled_with_fee_count: number;
}

export interface AnalyticsByClientRow {
  tenant_id: string;
  name: string;
  currency: string;
  orders: number;
  completed: number;
  cancelled: number;
  delivery_fees: number;
  client_charges: number;
  outstanding: number;
}

export interface PartnerAnalyticsSummary {
  kpis: AnalyticsKpis;
  series: ReportSeriesPoint[];
  by_client: AnalyticsByClientRow[];
  by_driver: ReportDriverRow[];
}

export async function fetchPartnerAnalyticsSummary(
  partnerId: string,
  range: PartnerDashboardDateRange,
  opts?: { tenantId?: string | null; fulfillment?: "delivery" | "pickup" | null }
): Promise<PartnerAnalyticsSummary> {
  const supabase = getSupabaseClient();
  const { fromIso, toIso } = rangeToInstants(range);
  const { data, error } = await supabase.rpc("partner_analytics_summary", {
    p_partner_id: partnerId,
    p_from: fromIso,
    p_to: toIso,
    p_tenant_id: opts?.tenantId ?? null,
    p_fulfillment: opts?.fulfillment ?? null,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  const kpis = asRecord(row.kpis) ?? {};
  const byClient = Array.isArray(row.by_client) ? row.by_client : [];
  return {
    kpis: {
      ...mapKpis(kpis),
      cod_total: num(kpis.cod_total),
      wallet_balance: num(kpis.wallet_balance),
      pending_added_in_range: num(kpis.pending_added_in_range),
      cancelled_with_fee_count: num(kpis.cancelled_with_fee_count),
    },
    series: mapSeries(row.series),
    by_client: byClient
      .map((item) => asRecord(item))
      .filter((r): r is Record<string, unknown> => !!r)
      .map((r) => ({
        tenant_id: str(r.tenant_id),
        name: str(r.name, "Client"),
        currency: str(r.currency, "IQD"),
        orders: num(r.orders),
        completed: num(r.completed),
        cancelled: num(r.cancelled),
        delivery_fees: num(r.delivery_fees),
        client_charges: num(r.client_charges),
        outstanding: num(r.outstanding),
      })),
    by_driver: mapReportDrivers(row.by_driver),
  };
}

// ── Analytics money layer (170) ──────────────────────────────────────────────

export interface AnalyticsMoneyDriverRow {
  driver_id: string;
  name: string;
  trips: number;
  driver_cost: number;
}

export interface PartnerAnalyticsMoney {
  delivery_income: number;
  client_charges: number;
  revenue_estimate: number;
  driver_cost: number;
  oelox_cost: number;
  driver_credit: number;
  driver_cash_collected: number;
  client_wallet_charge: number;
  settled_trips: number;
  voided_total: number;
  profit_estimate: number;
  margin_pct: number | null;
  /** False until a partner default or per-driver pay is configured. */
  has_rates: boolean;
  by_driver: AnalyticsMoneyDriverRow[];
}

export async function fetchPartnerAnalyticsMoney(
  partnerId: string,
  range: PartnerDashboardDateRange,
  opts?: { tenantId?: string | null; fulfillment?: "delivery" | "pickup" | null }
): Promise<PartnerAnalyticsMoney> {
  const supabase = getSupabaseClient();
  const { fromIso, toIso } = rangeToInstants(range);
  const { data, error } = await supabase.rpc("partner_analytics_money", {
    p_partner_id: partnerId,
    p_from: fromIso,
    p_to: toIso,
    p_tenant_id: opts?.tenantId ?? null,
    p_fulfillment: opts?.fulfillment ?? null,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  const byDriver = Array.isArray(row.by_driver) ? row.by_driver : [];
  return {
    delivery_income: num(row.delivery_income),
    client_charges: num(row.client_charges),
    revenue_estimate: num(row.revenue_estimate),
    driver_cost: num(row.driver_cost),
    oelox_cost: num(row.oelox_cost),
    driver_credit: num(row.driver_credit),
    driver_cash_collected: num(row.driver_cash_collected),
    client_wallet_charge: num(row.client_wallet_charge),
    settled_trips: num(row.settled_trips),
    voided_total: num(row.voided_total),
    profit_estimate: num(row.profit_estimate),
    margin_pct: row.margin_pct == null ? null : num(row.margin_pct),
    has_rates: row.has_rates === true,
    by_driver: byDriver
      .map((item) => asRecord(item))
      .filter((r): r is Record<string, unknown> => !!r)
      .map((r) => ({
        driver_id: str(r.driver_id),
        name: str(r.name, "Driver"),
        trips: num(r.trips),
        driver_cost: num(r.driver_cost),
      })),
  };
}

// ── Client wallet (partner ↔ client pre-paid balance) ────────────────────────

export interface PartnerClientWalletEntry {
  id: string;
  type: string;
  amount: number;
  order_id: string | null;
  description: string | null;
  created_at: string;
}

export interface PartnerClientWallet {
  balance: number;
  negative_limit: number;
  currency: string;
  transactions: PartnerClientWalletEntry[];
}

function mapClientWallet(data: unknown): PartnerClientWallet {
  const row = asRecord(data) ?? {};
  const wallet = asRecord(row.wallet) ?? row;
  const list = Array.isArray(row.transactions) ? row.transactions : [];
  return {
    balance: num(wallet.balance),
    negative_limit: num(wallet.negative_limit),
    currency: str(wallet.currency, "IQD"),
    transactions: list
      .map((item) => asRecord(item))
      .filter((r): r is Record<string, unknown> => !!r)
      .map((r) => ({
        id: str(r.id),
        type: str(r.type),
        amount: num(r.amount),
        order_id: r.order_id != null ? str(r.order_id) : null,
        description: r.description != null ? str(r.description) : null,
        created_at: str(r.created_at),
      })),
  };
}

export async function fetchPartnerClientWallet(
  partnerId: string,
  tenantId: string
): Promise<PartnerClientWallet> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_client_wallet_get", {
    p_partner_id: partnerId,
    p_tenant_id: tenantId,
  });
  if (error) throw error;
  return mapClientWallet(data);
}

export async function topupPartnerClientWallet(
  partnerId: string,
  tenantId: string,
  amount: number,
  note?: string
): Promise<PartnerClientWallet> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_client_wallet_topup", {
    p_partner_id: partnerId,
    p_tenant_id: tenantId,
    p_amount: amount,
    p_note: note?.trim() || null,
  });
  if (error) throw error;
  return mapClientWallet(data);
}

export async function setPartnerClientWalletLimit(
  partnerId: string,
  tenantId: string,
  negativeLimit: number
): Promise<PartnerClientWallet> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_client_wallet_set_limit", {
    p_partner_id: partnerId,
    p_tenant_id: tenantId,
    p_negative_limit: negativeLimit,
  });
  if (error) throw error;
  return mapClientWallet(data);
}

// ── Shared display helpers ───────────────────────────────────────────────────

/** Normalize delivery-fee responsibility for display. */
export function feeResponsibilityLabelKey(value: string | null):
  | "responsibility.customer"
  | "responsibility.client"
  | "responsibility.free"
  | "responsibility.oelox"
  | "responsibility.other" {
  const v = (value ?? "").trim().toUpperCase();
  if (v === "" || v === "CUSTOMER_PAYS") return "responsibility.customer";
  if (v === "RESTAURANT_PAYS") return "responsibility.client";
  if (v === "FREE_DELIVERY") return "responsibility.free";
  if (v === "OELOX_PAYS") return "responsibility.oelox";
  return "responsibility.other";
}

export function ledgerStatusLabelKey(value: string):
  | "ledger.status.pending"
  | "ledger.status.settled"
  | "ledger.status.instant"
  | "ledger.status.other" {
  if (value === "pending") return "ledger.status.pending";
  if (value === "settled") return "ledger.status.settled";
  if (value === "instant_deducted") return "ledger.status.instant";
  return "ledger.status.other";
}

export function paymentTypeLabel(type: {
  payment_method: string | null;
  payment_status: string | null;
  pickup_payment_mode: string | null;
}): "payment.prepaid" | "payment.cod" | "payment.cashier" | "payment.unknown" {
  const method = (type.payment_method ?? "").toLowerCase();
  const mode = (type.pickup_payment_mode ?? "").trim().toUpperCase();
  if (type.payment_status === "paid" || method === "card" || method === "wallet") {
    return "payment.prepaid";
  }
  if (mode === "COD" || method === "cod") return "payment.cod";
  if (mode === "PAY_AT_CASHIER") return "payment.cashier";
  return "payment.unknown";
}

export function formatDayLabel(day: string): string {
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(d);
}

export function formatShortDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export function clientStatusLabelKey(status: string): TranslationKey {
  const s = status.toLowerCase();
  if (s === "active" || s === "open" || s === "live") return "clients.active";
  if (s === "trial") return "clients.trial";
  if (s === "onboarding") return "clients.onboarding";
  if (s === "inactive" || s === "closed" || s === "suspended") {
    return "clients.inactive";
  }
  if (s === "pending") return "orders.stages.pending";
  return "clients.unknownStatus";
}

export function isClientActiveStatus(status: string): boolean {
  const s = status.toLowerCase();
  return s === "active" || s === "open" || s === "live";
}

/** Status pill variant — deliberately NOT the green presence style (that means driver online). */
export function clientStatusPillVariant(
  status: string
): "info" | "pending" | "danger" {
  const s = status.toLowerCase();
  if (s === "active" || s === "open" || s === "live") return "info";
  if (s === "trial" || s === "onboarding" || s === "pending") return "pending";
  return "danger";
}

export function feeTypeLabelKey(value: string | null):
  | "fees.perOrder"
  | "fees.percentage"
  | "fees.unknown" {
  if (value === "per_order") return "fees.perOrder";
  if (value === "percentage") return "fees.percentage";
  return "fees.unknown";
}

export function settlementModeLabelKey(value: string | null):
  | "fees.instant"
  | "fees.credit"
  | "fees.unknown" {
  if (value === "instant") return "fees.instant";
  if (value === "credit") return "fees.credit";
  return "fees.unknown";
}

export function cashTargetLabelKey(value: string | null):
  | "fees.targetBusiness"
  | "fees.targetPartner"
  | "fees.unknown" {
  if (value === "business") return "fees.targetBusiness";
  if (value === "partner") return "fees.targetPartner";
  return "fees.unknown";
}
